import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Camera, ChevronRight, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Loader, Card, LevelBadge, PhotoThumb } from "@/components/Common";

const TYPES = ["Irrigazione", "Concimazione", "Potatura", "Rinvaso", "Trattamento", "Spostamento", "Altro"];

function Interventions({ plant, reload }) {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), type: "Irrigazione", description: "" });
  const add = async () => {
    if (!form.description) return toast.error("Descrivi l'intervento");
    await api.post(`/plants/${plant.id}/interventions`, form).catch((e) => toast.error(errMsg(e)));
    setForm({ ...form, description: "" });
    reload();
  };
  const del = async (id) => {
    await api.delete(`/interventions/${id}`);
    reload();
  };
  return (
    <Card testid="interventions-card">
      <h3 className="font-serif text-xl font-semibold text-[#0D2517]">Interventi eseguiti</h3>
      <div className="mt-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <input type="date" className="field" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="intervention-date" />
          <select className="field" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="intervention-type">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <input className="field" placeholder="Descrizione" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="intervention-description" />
        <button onClick={add} className="btn-secondary w-full !py-2.5" data-testid="intervention-add-button"><Plus size={14} /> Aggiungi intervento</button>
      </div>
      <ul className="mt-4 divide-y divide-[#ddc69c]/50">
        {plant.interventions.map((i) => (
          <li key={i.id} className="flex items-start justify-between py-2 text-sm" data-testid={`intervention-${i.id}`}>
            <span><strong>{i.type}</strong> · {fmtDate(i.date)}<br /><span className="text-[#5b6b60]">{i.description}</span></span>
            <button onClick={() => del(i.id)} data-testid={`intervention-delete-${i.id}`}><Trash2 size={14} className="text-[#8a958d]" /></button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function PlantDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(() => api.get(`/plants/${id}`).then(({ data }) => setP(data)).catch(() => navigate("/piante")), [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (!p) return <Loader />;
  const remove = async () => {
    if (!window.confirm("Eliminare la pianta? Le analisi resteranno nel tuo archivio.")) return;
    await api.delete(`/plants/${p.id}`);
    navigate("/piante");
  };

  return (
    <div className="space-y-5 px-5 py-6" data-testid="plant-detail-page">
      <div>
        <p className="eyebrow">Scheda pianta</p>
        <h1 className="font-serif text-4xl font-semibold text-[#0D2517]" data-testid="plant-name">{p.name}</h1>
        <p className="botanical text-lg text-[#B39257]">{p.species_common} {p.species_botanical && `(${p.species_botanical})`}</p>
        <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-[#5b6b60]">
          {p.cultivation && <span className="chip !py-1">{p.cultivation === "vaso" ? "In vaso" : "Piena terra"}</span>}
          {p.variety && <span className="chip !py-1">Varietà: {p.variety}</span>}
          {p.location && <span className="chip !py-1">{p.location}</span>}
          <span className="chip !py-1">Dal {fmtDate(p.created_at)}</span>
        </div>
      </div>

      <Link to={`/analisi/nuova?pianta=${p.id}`} className="btn-primary w-full" data-testid="plant-new-analysis-button"><Camera size={16} /> NUOVA ANALISI DI QUESTA PIANTA</Link>

      {p.photos.length > 0 && (
        <section data-testid="plant-photo-timeline">
          <p className="eyebrow">Fotografie nel tempo</p>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {p.photos.map((ph) => (
              <div key={ph.id} className="shrink-0">
                <PhotoThumb id={ph.id} className="h-24 w-24" />
                <p className="mt-1 text-center text-[10px] text-[#8a958d]">{fmtDate(ph.created_at)}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <Card testid="plant-history-card">
        <h3 className="font-serif text-xl font-semibold text-[#0D2517]">Storico analisi e problemi</h3>
        <div className="mt-3 space-y-2">
          {p.analyses.map((a) => (
            <Link key={a.id} to={`/analisi/${a.id}`} className="flex items-center gap-3 rounded-xl border border-[#ddc69c]/70 p-3" data-testid={`plant-analysis-${a.id}`}>
              <div className="flex-1">
                <p className="text-sm font-semibold">{a.result?.main_cause?.name || (a.result ? "Causa non determinata" : "Analisi in corso")}</p>
                <p className="text-[11px] text-[#8a958d]">{fmtDate(a.created_at)}</p>
              </div>
              {a.result && <LevelBadge level={a.result.overall_confidence || a.result.confidence} label="Att." />}
              <ChevronRight size={16} className="text-[#C9A96E]" />
            </Link>
          ))}
        </div>
      </Card>

      <Interventions plant={p} reload={load} />

      <button onClick={remove} className="w-full text-center text-xs text-[#B91C1C] underline" data-testid="plant-delete-button">Elimina pianta</button>
    </div>
  );
}
